import { useState } from 'react';
import { supabase, isOnline } from '../lib/supabase';

const MAX_SIZE = 1200;
const JPEG_QUALITY = 0.85;

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      if (width > MAX_SIZE || height > MAX_SIZE) {
        const ratio = Math.min(MAX_SIZE / width, MAX_SIZE / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Failed to resize image'));
          resolve(blob);
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

export function useImageUpload() {
  const [uploading, setUploading] = useState(false);

  const uploadRecipeImage = async (file, householdId, recipeId) => {
    if (!isOnline()) return { error: 'No connection' };

    setUploading(true);
    try {
      const blob = await resizeImage(file);
      const path = `${householdId}/${recipeId}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('recipe-images')
        .upload(path, blob, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) {
        return { error: uploadError.message };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('recipe-images')
        .getPublicUrl(path);

      return { url: publicUrl };
    } catch (err) {
      return { error: err.message };
    } finally {
      setUploading(false);
    }
  };

  const deleteRecipeImage = async (url) => {
    if (!isOnline() || !url) return;

    // Extract path from public URL: .../recipe-images/{household}/{recipe}.jpg
    const match = url.match(/recipe-images\/(.+)$/);
    if (!match) return;

    await supabase.storage.from('recipe-images').remove([match[1]]);
  };

  const uploadMemberAvatar = async (file, householdId, memberId) => {
    if (!isOnline()) return { error: 'No connection' };

    setUploading(true);
    try {
      const blob = await resizeImage(file);
      const path = `${householdId}/${memberId}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('member-avatars')
        .upload(path, blob, { contentType: 'image/jpeg', upsert: true });

      if (uploadError) return { error: uploadError.message };

      const { data: { publicUrl } } = supabase.storage
        .from('member-avatars')
        .getPublicUrl(path);

      // Append a cache-buster so an overwritten photo refreshes in <img>.
      return { url: `${publicUrl}?v=${blob.size}` };
    } catch (err) {
      return { error: err.message };
    } finally {
      setUploading(false);
    }
  };

  const deleteMemberAvatar = async (url) => {
    if (!isOnline() || !url) return;
    const match = url.split('?')[0].match(/member-avatars\/(.+)$/);
    if (!match) return;
    await supabase.storage.from('member-avatars').remove([match[1]]);
  };

  return { uploading, uploadRecipeImage, deleteRecipeImage, uploadMemberAvatar, deleteMemberAvatar };
}
